import { useEffect, useMemo } from "react";
import { useSearchParams, useLocation, useParams } from "react-router-dom";
import TradingLayout from "./components/trading-layout";
import { ChartDataProvider } from "./components/chart/chart-data-context";

// Stub for user store - replace with actual implementation when available
const useUserStore = () => ({
  hasKyc: false,
  canAccessFeature: () => true,
  user: null,
});

// Stub for config store - replace with actual implementation when available
const useConfigStore = () => ({
  settings: { kycStatus: false },
});

// Stub for settings hook - replace with actual implementation when available
const useSettings = () => {};

// Stub for KYC requirement check - replace with actual implementation when available
const getKycRequirement = (_user: any, _feature: string) => ({
  required: false,
});

// Stub for KYC notice component
const KycRequiredNotice = ({ feature }: { feature: string }) => (
  <div className="p-6 text-center">
    <h2 className="text-lg font-semibold">KYC Required</h2>
    <p className="text-muted-foreground">Feature: {feature}</p>
  </div>
);

export function TradePage() {
  // 1. Always call all hooks first
  const { user } = useUserStore();
  const { settings } = useConfigStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { companyPrefix, symbol, pair, currency } = useParams();
  const routeSymbol = symbol ?? companyPrefix;

  // Initialize settings early to prevent chart loading issues
  useSettings();

  const { requiredFeature } = useMemo(() => {
    if (location.pathname.startsWith("/binary")) {
      return { requiredFeature: "binary_trading" };
    }
    const type = searchParams.get("type");
    if (type === "futures") {
      return {
        requiredFeature: "futures_trading",
      };
    }
    return { requiredFeature: "trade" };
  }, [location.pathname, searchParams]);

  const kycEnabled = String(settings?.kycStatus) === "true" || String(settings?.kycStatus) === "1";

  // 2. Websocket cleanup hook — always called!
  useEffect(() => {
    return () => {
      const openWebSockets = Array.from(
        document.querySelectorAll(".chart-container")
      )
        .map((container) => (container as any).__chartWebSocket)
        .filter(Boolean);

      openWebSockets.forEach((ws) => {
        try {
          ws.close();
        } catch (e) {
          // ignore
        }
      });
    };
  }, []);

  // 3. Now branch and return
  // Only check KYC for logged-in users who want to actually trade
  // Visitors can view the trade page without KYC
  if (kycEnabled && user) {
    const kycRequirement = getKycRequirement(user, requiredFeature);
    if (kycRequirement.required) {
      return <KycRequiredNotice feature={requiredFeature} />;
    }
  }

  return (
    <ChartDataProvider>
      <div className="h-full w-full">
        <TradingLayout 
          symbol={routeSymbol} 
          currency={currency} 
          pair={pair} 
        />
      </div>
    </ChartDataProvider>
  );
}
